/**
 * Общий реестр-таблица для ERP-разделов (первый потребитель — views/tenders.js).
 * Данные грузит вызывающий через fetch(query) → { total, items }, поэтому таблица
 * одинаково работает с серверной пагинацией (limit/offset) и с локальной.
 *
 *   const table = createDataTable(el, {
 *     storageKey: 'ccpm_dt_tenders',
 *     columns: [{ key, label, width?, align?, sortKey?, hidden?, render?(row) → html, title?(row) }],
 *     filters: [{ key, label, multi?, options: [{ value, label }] }],   // чипы
 *       или { key, label, type: 'list', placeholder?, emptyValue?, emptyLabel? } — выпадающий список с поиском
 *       и чекбоксами (Р-1); варианты — ключи facets counts[key] (value → число), '' не бывает:
 *       «не определено» вызывающий отдаёт отдельным value (emptyValue) с подписью emptyLabel.
 *     closedToggle: { label: 'кроме закрытых' },                         // чекбокс, включён по умолчанию
 *     actions: true,                                                     // слот кнопок раздела в тулбаре
 *     search: { placeholder },
 *     pageSizes: [50, 100, 200],
 *     defaultSort: '-created_at',
 *     initialState: { filters: { status: ['decision'] } },               // перекрывает сохранённое
 *     fetch: async ({ limit, offset, sort, filters, q, hideClosed }) => ({ total, items }),
 *     rowId: r => r.id, rowClass?: r => '', onRowClick?: (row) => {},
 *     emptyHint: 'html',                                                 // пустое состояние
 *     facets: async (query) => ({ counts: { status: { decision: 3 } }, summary: 'Всего 5' }),
 *   });
 *
 * facets (UI-2) — числа у чипов (counts[filterKey][option.value]) и строка под таблицей;
 * вызывается после каждой загрузки и раз в 60 с. Колонки перетаскиваются за заголовок,
 * ширина меняется за правую границу заголовка; порядок и ширины — в том же storageKey.
 *   table.reload(); table.getState(); table.setSelected(id); table.updateRow(row); table.getRow(id);
 *   table.setActions(html);   // кнопки раздела; клики обрабатывает вызывающий
 *
 * Состояние (сортировка, фильтры, поиск, «кроме закрытых», колонки, их порядок и ширины, размер страницы)
 * хранится в localStorage под storageKey; ошибки storage не мешают работе.
 */
const VERSION = 1;
const FACETS_INTERVAL_MS = 60_000;
const MIN_COL_WIDTH = 48;
// УИ-3: мало фильтров (≤ порога) — чипы инлайн в строке поиска; много — отдельной
// компактной строкой под тулбаром, чтобы первичные кнопки не уезжали под фильтры.
const INLINE_FILTER_MAX = 3;

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function loadState(key) {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || 'null');
    return parsed && parsed.version === VERSION ? parsed : null;
  } catch {
    return null;
  }
}

function saveState(key, state) {
  try {
    localStorage.setItem(key, JSON.stringify({ version: VERSION, ...state }));
  } catch {
    /* storage недоступен — состояние живёт до перезагрузки */
  }
}

export function createDataTable(container, cfg) {
  const pageSizes = cfg.pageSizes ?? [50, 100, 200];
  const defaults = {
    sort:       cfg.defaultSort ?? null,
    filters:    {},
    q:          '',
    hideClosed: true,
    hidden:     cfg.columns.filter(c => c.hidden).map(c => c.key),
    order:      cfg.columns.map(c => c.key),
    widths:     {},
    pageSize:   pageSizes[0],
  };
  const saved = cfg.storageKey ? loadState(cfg.storageKey) : null;
  const state = { ...defaults, ...(saved ?? {}), ...(cfg.initialState ?? {}), offset: 0 };
  if (cfg.initialState?.filters) state.filters = { ...cfg.initialState.filters };
  if (!pageSizes.includes(state.pageSize)) state.pageSize = pageSizes[0];

  let rows = [];
  let total = 0;
  let selectedId = null;
  let loadSeq = 0;
  let searchTimer = null;
  let facetCounts = {};
  let facetSeq = 0;
  let facetsTimer = null;
  let suppressSortClick = false;
  let openList = null;  // key открытого list-фильтра (переживает перерисовку чипов)
  let listQ = '';

  const persist = () => {
    if (!cfg.storageKey) return;
    const { sort, filters, q, hideClosed, hidden, order, widths, pageSize } = state;
    saveState(cfg.storageKey, { sort, filters, q, hideClosed, hidden, order, widths, pageSize });
  };

  const byKey = Object.fromEntries(cfg.columns.map(c => [c.key, c]));
  /** Колонки в сохранённом порядке; новые (которых нет в сохранении) — в конце. */
  const orderedCols = () => {
    const known = state.order.filter(k => byKey[k]);
    return [...known, ...cfg.columns.map(c => c.key).filter(k => !known.includes(k))].map(k => byKey[k]);
  };
  const visibleCols = () => orderedCols().filter(c => !state.hidden.includes(c.key));
  const colWidth = (c) => state.widths[c.key] ?? c.width ?? 120;
  const rowId = cfg.rowId ?? (r => r.id);

  // Чипы-фильтры: ≤ INLINE_FILTER_MAX — в тулбаре рядом с поиском; больше — отдельной
  // строкой .dt-filters под тулбаром (одна .dt-chips в любом случае — renderChips её найдёт).
  const manyFilters = (cfg.filters?.length ?? 0) > INLINE_FILTER_MAX;
  container.innerHTML = `
    <div class="dt">
      <div class="dt-toolbar">
        ${cfg.search ? `<input type="search" class="dt-search" placeholder="${esc(cfg.search.placeholder ?? 'Поиск')}" value="${esc(state.q)}">` : ''}
        ${manyFilters ? '' : '<div class="dt-chips"></div>'}
        ${cfg.closedToggle ? `<label class="dt-closed"><input type="checkbox" data-dt-closed${state.hideClosed ? ' checked' : ''}> ${esc(cfg.closedToggle.label ?? 'кроме закрытых')}</label>` : ''}
        ${cfg.actions ? '<div class="dt-actions"></div>' : ''}
        <div class="dt-columns">
          <button type="button" class="dt-btn" data-dt-columns aria-expanded="false">Колонки ▾</button>
          <div class="dt-columns-menu" hidden></div>
        </div>
      </div>
      ${manyFilters ? '<div class="dt-filters"><div class="dt-chips"></div></div>' : ''}
      <div class="dt-scroll"><table class="dt-table"><thead></thead><tbody></tbody></table>
        <div class="dt-empty" hidden></div>
      </div>
      <div class="dt-footer">
        <span class="dt-summary" data-dt-summary></span>
        <span class="dt-range"></span>
        <button type="button" class="dt-btn" data-dt-page="-1" title="Назад">‹</button>
        <button type="button" class="dt-btn" data-dt-page="1" title="Вперёд">›</button>
        <label class="dt-pagesize">по <select data-dt-pagesize>
          ${pageSizes.map(n => `<option value="${n}"${n === state.pageSize ? ' selected' : ''}>${n}</option>`).join('')}
        </select></label>
      </div>
    </div>`;

  const root = container.querySelector('.dt');
  const $ = (sel) => root.querySelector(sel);

  function listLabel(f, v) {
    return v === f.emptyValue ? (f.emptyLabel ?? '—') : v;
  }

  function listMatches(f, v, q) {
    return !q || listLabel(f, v).toLowerCase().includes(q.toLowerCase());
  }

  function renderList(f) {
    const sel = state.filters[f.key] ?? [];
    const counts = facetCounts[f.key] ?? {};
    const values = [...new Set([...Object.keys(counts), ...sel])]
      .sort((a, b) => (counts[b] ?? 0) - (counts[a] ?? 0) || listLabel(f, a).localeCompare(listLabel(f, b), 'ru'));
    const caption = !sel.length ? 'все' : sel.length === 1 ? listLabel(f, sel[0]) : `выбрано ${sel.length}`;
    const open = openList === f.key;
    return `
      <span class="dt-chip-group dt-list" data-filter="${esc(f.key)}">
        <span class="dt-chip-label">${esc(f.label)}</span>
        <button type="button" class="dt-chip${sel.length ? ' is-on' : ''}" data-dt-list="${esc(f.key)}" aria-expanded="${open}">${esc(caption)} ▾</button>
        <div class="dt-list-menu" data-dt-list-menu="${esc(f.key)}"${open ? '' : ' hidden'}>
          <input type="search" class="dt-list-q" data-dt-list-q="${esc(f.key)}" placeholder="${esc(f.placeholder ?? 'Поиск')}" value="${esc(listQ)}">
          <div class="dt-list-opts">
            ${values.map(v => `<label class="dt-col-opt dt-list-opt" data-list-label="${esc(listLabel(f, v))}"${listMatches(f, v, listQ) ? '' : ' hidden'}>
              <input type="checkbox" data-list-opt="${esc(f.key)}" value="${esc(v)}"${sel.includes(v) ? ' checked' : ''}>
              <span class="dt-list-name">${esc(listLabel(f, v))}</span>
              <span class="dt-chip-count">${esc(counts[v] ?? 0)}</span></label>`).join('') || '<span class="dt-list-none">Нет вариантов</span>'}
          </div>
          ${sel.length ? '<button type="button" class="dt-btn" data-dt-list-clear>Сбросить</button>' : ''}
        </div>
      </span>`;
  }

  function renderChips() {
    $('.dt-chips').innerHTML = (cfg.filters ?? []).map(f => (f.type === 'list' ? renderList(f) : `
      <span class="dt-chip-group" data-filter="${esc(f.key)}">
        <span class="dt-chip-label">${esc(f.label)}</span>
        ${f.options.map(o => {
          const on = (state.filters[f.key] ?? []).includes(o.value);
          const n = facetCounts[f.key]?.[o.value];
          const count = n == null ? '' : ` <span class="dt-chip-count" data-chip-count>${esc(n)}</span>`;
          return `<button type="button" class="dt-chip${on ? ' is-on' : ''}" data-chip="${esc(f.key)}" data-value="${esc(o.value)}" aria-pressed="${on}">${esc(o.label)}${count}</button>`;
        }).join('')}
      </span>`)).join('');
    if (openList) {
      const q = root.querySelector(`[data-dt-list-q="${CSS.escape(openList)}"]`);
      if (q && document.activeElement !== q && listFocus) { q.focus(); q.setSelectionRange(q.value.length, q.value.length); }
    }
  }
  let listFocus = false;

  function renderColumnsMenu() {
    $('.dt-columns-menu').innerHTML = orderedCols().map(c => `
      <label class="dt-col-opt"><input type="checkbox" data-col="${esc(c.key)}"${state.hidden.includes(c.key) ? '' : ' checked'}> ${esc(c.label)}</label>`).join('')
      + '<button type="button" class="dt-btn dt-col-reset" data-dt-reset>Сбросить колонки</button>';
  }

  function renderHead() {
    const [sortKey, desc] = state.sort ? [state.sort.replace(/^-/, ''), state.sort.startsWith('-')] : [null, false];
    const cols = visibleCols();
    $('thead').innerHTML = `<tr>${cols.map(c => {
      const sortable = !!c.sortKey;
      const active = sortable && c.sortKey === sortKey;
      const arrow = active ? (desc ? ' ↓' : ' ↑') : '';
      return `<th data-key="${esc(c.key)}" draggable="true"${sortable ? ` data-sort="${esc(c.sortKey)}" class="dt-sortable${active ? ' is-sorted' : ''}"` : ''}
        style="width:${colWidth(c)}px;${c.align ? `text-align:${c.align};` : ''}"><span class="dt-th-label">${esc(c.label)}${arrow}</span><span class="dt-resize" data-dt-resize="${esc(c.key)}" title="Ширина колонки"></span></th>`;
    }).join('')}</tr>`;
    syncTableWidth();
  }

  function syncTableWidth() {
    const w = [...root.querySelectorAll('thead th')].reduce((n, th) => n + (parseFloat(th.style.width) || 0), 0);
    $('.dt-table').style.width = `${w}px`;
  }

  function cellHtml(c, r) {
    const v = c.render ? c.render(r) : esc(r[c.key] ?? '');
    const title = c.title ? ` title="${esc(c.title(r))}"` : '';
    return `<td data-key="${esc(c.key)}"${c.align ? ` style="text-align:${c.align}"` : ''}${title}>${v}</td>`;
  }

  function rowHtml(r) {
    const id = rowId(r);
    const cls = ['dt-row', cfg.rowClass?.(r), id === selectedId ? 'is-selected' : ''].filter(Boolean).join(' ');
    return `<tr class="${cls}" data-id="${esc(id)}">${visibleCols().map(c => cellHtml(c, r)).join('')}</tr>`;
  }

  function renderBody() {
    $('tbody').innerHTML = rows.map(rowHtml).join('');
    const empty = $('.dt-empty');
    empty.hidden = rows.length > 0;
    if (!rows.length) {
      const filtered = state.q || Object.values(state.filters).some(v => v?.length);
      empty.innerHTML = filtered
        ? 'Ничего не найдено. Снимите фильтры или измените поиск.'
        : (cfg.emptyHint ?? 'Записей пока нет.');
    }
    const from = total ? state.offset + 1 : 0;
    const to = Math.min(state.offset + rows.length, total);
    $('.dt-range').textContent = `${from}–${to} из ${total}`;
    $('[data-dt-page="-1"]').disabled = state.offset <= 0;
    $('[data-dt-page="1"]').disabled = state.offset + state.pageSize >= total;
  }

  async function reload() {
    const seq = ++loadSeq;
    root.classList.add('is-loading');
    try {
      const res = await cfg.fetch({
        limit: state.pageSize, offset: state.offset, sort: state.sort,
        filters: state.filters, q: state.q.trim(), hideClosed: state.hideClosed,
      });
      if (seq !== loadSeq) return;
      rows = res.items ?? [];
      total = res.total ?? rows.length;
      renderBody();
      loadFacets();
    } catch (e) {
      if (seq !== loadSeq || e.message === '401') return;
      rows = [];
      total = 0;
      renderBody();
      $('.dt-empty').textContent = `Ошибка загрузки: ${e.detail ?? e.message}`;
    } finally {
      if (seq === loadSeq) root.classList.remove('is-loading');
    }
  }

  const refetch = () => { state.offset = 0; persist(); reload(); };

  async function loadFacets() {
    if (!cfg.facets || !document.body.contains(root)) return;
    const seq = ++facetSeq;
    try {
      const res = await cfg.facets({
        sort: state.sort, filters: state.filters, q: state.q.trim(), hideClosed: state.hideClosed,
      });
      if (seq !== facetSeq) return;
      facetCounts = res?.counts ?? {};
      renderChips();
      $('[data-dt-summary]').textContent = res?.summary ?? '';
    } catch {
      /* счётчики некритичны */
    }
  }

  if (cfg.facets) {
    facetsTimer = setInterval(() => {
      if (!document.body.contains(root)) { clearInterval(facetsTimer); return; }
      if (!document.hidden) loadFacets();
    }, FACETS_INTERVAL_MS);
  }

  function resetColumns() {
    state.order = [...defaults.order];
    state.widths = {};
    state.hidden = [...defaults.hidden];
    persist();
    renderColumnsMenu();
    renderHead();
    renderBody();
  }

  // ── Ширина колонки: тянуть правую границу заголовка ──
  let resizing = false;
  // mousedown на ручке не должен начинать HTML5-перетаскивание заголовка.
  root.addEventListener('mousedown', (e) => {
    if (e.target instanceof Element && e.target.closest('[data-dt-resize]')) e.preventDefault();
  });
  root.addEventListener('pointerdown', (e) => {
    const handle = e.target instanceof Element && e.target.closest('[data-dt-resize]');
    if (!handle) return;
    e.preventDefault();
    e.stopPropagation();
    const th = handle.closest('th');
    const key = handle.dataset.dtResize;
    const startX = e.clientX;
    const startW = th.getBoundingClientRect().width;
    th.draggable = false;
    resizing = true;
    root.classList.add('is-resizing');
    handle.setPointerCapture?.(e.pointerId);
    const move = (ev) => {
      th.style.width = `${Math.max(MIN_COL_WIDTH, Math.round(startW + ev.clientX - startX))}px`;
      syncTableWidth();
    };
    const up = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
      root.classList.remove('is-resizing');
      resizing = false;
      th.draggable = true;
      state.widths = { ...state.widths, [key]: parseFloat(th.style.width) };
      suppressSortClick = true;
      setTimeout(() => { suppressSortClick = false; }, 0);
      persist();
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
  });

  // ── Порядок колонок: перетащить заголовок на другой ──
  let dragKey = null;
  const clearDrop = () => root.querySelectorAll('th.dt-drop-before, th.dt-drop-after')
    .forEach(th => th.classList.remove('dt-drop-before', 'dt-drop-after'));
  const dropSide = (th, e) => {
    const r = th.getBoundingClientRect();
    return e.clientX < r.left + r.width / 2 ? 'before' : 'after';
  };
  root.addEventListener('dragstart', (e) => {
    const th = e.target instanceof Element && e.target.closest('thead th[data-key]');
    if (!th) return;
    if (resizing) { e.preventDefault(); return; }
    dragKey = th.dataset.key;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', dragKey);
    th.classList.add('dt-dragging');
  });
  root.addEventListener('dragover', (e) => {
    const th = dragKey && e.target instanceof Element && e.target.closest('thead th[data-key]');
    if (!th) return;
    e.preventDefault();
    clearDrop();
    if (th.dataset.key !== dragKey) th.classList.add(`dt-drop-${dropSide(th, e)}`);
  });
  root.addEventListener('drop', (e) => {
    const th = dragKey && e.target instanceof Element && e.target.closest('thead th[data-key]');
    if (!th) return;
    e.preventDefault();
    const target = th.dataset.key;
    if (target !== dragKey) {
      const order = orderedCols().map(c => c.key).filter(k => k !== dragKey);
      order.splice(order.indexOf(target) + (dropSide(th, e) === 'after' ? 1 : 0), 0, dragKey);
      state.order = order;
      persist();
      renderColumnsMenu();
      renderHead();
      renderBody();
    }
  });
  root.addEventListener('dragend', () => {
    dragKey = null;
    clearDrop();
    root.querySelectorAll('th.dt-dragging').forEach(th => th.classList.remove('dt-dragging'));
  });

  root.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const chip = t.closest('[data-chip]');
    if (chip) {
      const f = cfg.filters.find(x => x.key === chip.dataset.chip);
      const cur = state.filters[f.key] ?? [];
      const v = chip.dataset.value;
      const next = cur.includes(v) ? cur.filter(x => x !== v) : (f.multi ? [...cur, v] : [v]);
      if (next.length) state.filters[f.key] = next; else delete state.filters[f.key];
      renderChips();
      refetch();
      return;
    }
    const listBtn = t.closest('[data-dt-list]');
    if (listBtn) {
      const key = listBtn.dataset.dtList;
      openList = openList === key ? null : key;
      listQ = '';
      listFocus = !!openList;
      renderChips();
      return;
    }
    if (t.closest('[data-dt-list-clear]')) {
      delete state.filters[openList];
      renderChips();
      refetch();
      return;
    }
    if (t.closest('[data-dt-reset]')) { resetColumns(); return; }
    const th = t.closest('th[data-sort]');
    if (th && !suppressSortClick && !t.closest('[data-dt-resize]')) {
      const key = th.dataset.sort;
      state.sort = state.sort === key ? `-${key}` : key;
      renderHead();
      refetch();
      return;
    }
    const page = t.closest('[data-dt-page]');
    if (page) {
      state.offset = Math.max(0, state.offset + Number(page.dataset.dtPage) * state.pageSize);
      reload();
      return;
    }
    const colsBtn = t.closest('[data-dt-columns]');
    if (colsBtn) {
      const menu = $('.dt-columns-menu');
      menu.hidden = !menu.hidden;
      colsBtn.setAttribute('aria-expanded', String(!menu.hidden));
      return;
    }
    const tr = t.closest('tr.dt-row');
    if (tr && cfg.onRowClick && !t.closest('a')) {
      const row = rows.find(r => String(rowId(r)) === tr.dataset.id);
      if (row) cfg.onRowClick(row);
    }
  });

  root.addEventListener('change', (e) => {
    const t = e.target;
    if (t.matches('[data-list-opt]')) {
      const key = t.dataset.listOpt;
      const cur = (state.filters[key] ?? []).filter(v => v !== t.value);
      const next = t.checked ? [...cur, t.value] : cur;
      if (next.length) state.filters[key] = next; else delete state.filters[key];
      listFocus = false;
      renderChips();
      refetch();
      return;
    }
    if (t.matches('[data-dt-closed]')) { state.hideClosed = t.checked; refetch(); return; }
    if (t.matches('[data-dt-pagesize]')) { state.pageSize = Number(t.value); refetch(); return; }
    if (t.matches('[data-col]')) {
      const key = t.dataset.col;
      state.hidden = t.checked ? state.hidden.filter(k => k !== key) : [...state.hidden, key];
      persist();
      renderHead();
      renderBody();
    }
  });

  root.addEventListener('input', (e) => {
    if (e.target.matches('[data-dt-list-q]')) {
      // Фильтруем варианты на месте, без перерисовки (фокус и курсор остаются).
      listQ = e.target.value;
      e.target.closest('.dt-list-menu').querySelectorAll('[data-list-label]').forEach(el => {
        el.hidden = !el.dataset.listLabel.toLowerCase().includes(listQ.toLowerCase());
      });
      return;
    }
    if (!e.target.matches('.dt-search')) return;
    state.q = e.target.value;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(refetch, 300);
  });

  // Клик вне меню колонок закрывает его. Слушатель снимается, когда таблица ушла из DOM.
  const onDocClick = (e) => {
    if (!document.body.contains(root)) { document.removeEventListener('click', onDocClick); return; }
    const menu = $('.dt-columns-menu');
    if (!menu.hidden && !e.target.closest?.('.dt-columns')) menu.hidden = true;
    if (openList && !e.target.closest?.('.dt-list') && e.target.isConnected) {
      openList = null;
      listQ = '';
      renderChips();
    }
  };
  document.addEventListener('click', onDocClick);

  renderChips();
  renderColumnsMenu();
  renderHead();
  renderBody();
  reload();

  return {
    reload,
    reloadFacets: loadFacets,
    getState: () => ({ ...state, filters: { ...state.filters } }),
    setSelected(id) {
      selectedId = id;
      root.querySelectorAll('tr.dt-row').forEach(tr => {
        tr.classList.toggle('is-selected', String(id) === tr.dataset.id);
      });
    },
    setActions(html) {
      const el = root.querySelector('.dt-actions');
      if (el) el.innerHTML = html;
    },
    updateRow(row) {
      const i = rows.findIndex(r => rowId(r) === rowId(row));
      if (i < 0) return;
      rows[i] = { ...rows[i], ...row };
      const tr = root.querySelector(`tr.dt-row[data-id="${CSS.escape(String(rowId(row)))}"]`);
      if (tr) tr.outerHTML = rowHtml(rows[i]);
    },
    getRow: (id) => rows.find(r => rowId(r) === id),
  };
}
